import {
  forgotPasswordApiV1AuthForgotPasswordPost,
  resetPasswordApiV1AuthResetPasswordPost,
} from '@closet-theory/api';
import { useState } from 'react';
import type { FormEvent, PropsWithChildren } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, PageHeader } from '@ui/console/primitives';
import { TextField } from '@ui/console/forms';
import { configurePublicStylistApi } from './auth';
import { Actions } from './components';

function textField(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
}

function AuthPage({
  title,
  description,
  children,
}: PropsWithChildren<{ title: string; description: string }>) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-secondary px-4 py-10">
      <div className="flex w-full max-w-md flex-col gap-6">
        <a href="/" className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-solid text-sm font-bold text-white">
            CT
          </span>
          <span className="flex min-w-0 flex-col leading-tight">
            <strong className="truncate text-sm font-semibold text-primary">Closet Theory</strong>
            <span className="truncate text-xs text-tertiary">Staff access</span>
          </span>
        </a>
        <PageHeader title={title} description={description} />
        {children}
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = textField(new FormData(event.currentTarget), 'email');
    configurePublicStylistApi();
    const result = await forgotPasswordApiV1AuthForgotPasswordPost({ body: { email } });
    if (result.error) {
      setMessage(undefined);
      setError('The reset request could not be completed.');
      return;
    }
    setError(undefined);
    setMessage('Check your email for the reset link.');
  };
  return (
    <AuthPage
      title="Reset access"
      description="Enter your work email and we will send you a link for setting a new password. The link stops working after a short while, so use it soon after it arrives."
    >
      <form
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-5"
      >
        <TextField label="Email" name="email" type="email" autoComplete="username" required />
        {error ? <Callout tone="error" title={error} /> : null}
        {message ? <Callout tone="success" title={message} /> : null}
        <Actions>
          <Button size="sm" color="link-gray" href="/sign-in">
            Back to sign in
          </Button>
          <Button size="sm" color="primary" type="submit">
            Send reset link
          </Button>
        </Actions>
      </form>
    </AuthPage>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const token = params.get('token') ?? '';
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) {
      setError('This reset link is missing its token.');
      return;
    }
    const password = textField(new FormData(event.currentTarget), 'password');
    configurePublicStylistApi();
    const result = await resetPasswordApiV1AuthResetPasswordPost({ body: { token, password } });
    if (result.error) {
      setError(
        result.response.status === 400
          ? 'This reset link is invalid or expired.'
          : 'The new password was rejected.',
      );
      return;
    }
    void navigate('/sign-in', { replace: true });
  };
  return (
    <AuthPage
      title="Choose a new password"
      description="Set the password you will use from now on. This page only works from a link we emailed you, and only once."
    >
      <form
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-4 rounded-xl border border-secondary bg-primary p-5"
      >
        {!token ? <Callout tone="error" title="This reset link is missing its token." /> : null}
        <TextField
          label="New password"
          name="password"
          type="password"
          autoComplete="new-password"
          minLength={12}
          maxLength={200}
          required
        />
        {error && token ? <Callout tone="error" title={error} /> : null}
        <Actions>
          <Button size="sm" color="primary" type="submit" isDisabled={!token}>
            Update password
          </Button>
        </Actions>
      </form>
    </AuthPage>
  );
}
